import {
    CameraOutlined,
    MailOutlined,
    PhoneOutlined,
    SaveOutlined,
    TeamOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Card, Col, Divider, Form, Input, Row, Tag, Typography, Upload } from 'antd';
import { useEffect, useState } from 'react';
import { modalError, modalSuccess } from '../../Component/Modal/ModalLayout';
import { apiCall } from '../../API/RestApi';
import { useSelector } from 'react-redux';

const { Title, Text } = Typography;

function Profile() {
    const token = useSelector((state) => state.user.token);
    const [previewImage, setPreviewImage] = useState();
    const [profile, setProfile] = useState({});
    const [form] = Form.useForm();
    const [file, setFile] = useState();
    const [parent, setParent] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        callApi('GET', '/profile', 'profile');
        callApi('GET', '/profileparent', 'parent');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFinish = (values) => {
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('username', values.username);
        formData.append('email', values.email);
        formData.append('phone', values.phone);
        if (file) formData.append('image', file);
        setSaving(true);
        callApi('PUT', '/profile', 'update', formData);
    };

    const callApi = (method, url, type, values) => {
        const dataReq = {
            method,
            url,
            data: {
                data: values,
                headers: {
                    Authorization: 'Bearer ' + token,
                    ...(values instanceof FormData ? {} : { 'Content-Type': 'application/json' })
                }
            }
        };

        apiCall(dataReq).then((res) => {
            if (res.status === 'success') {
                switch (type) {
                    case 'profile': {
                        const usr = res.data || {};
                        setProfile(usr);
                        setPreviewImage(usr.image || undefined);
                        form.setFieldsValue({
                            username: usr.username,
                            email: usr.email,
                            phone: usr.phone,
                            name: usr.name,
                        });
                        break;
                    }
                    case 'parent':
                        setParent(res.data || {});
                        break;
                    case 'update':
                        modalSuccess(res.message);
                        setFile(undefined);
                        callApi('GET', '/profile', 'profile');
                        break;
                    default:
                        break;
                }
            } else {
                modalError(res.message);
            }
        }).catch((e) => {
            console.log(e);
            if (type === 'update') modalError('Gagal menyimpan perubahan profile.');
        }).finally(() => {
            if (type === 'update') setSaving(false);
        });
    };

    const uploadImage = (info) => {
        const rawFile = info.file?.originFileObj || info.file;
        if (!rawFile) return;
        setFile(rawFile);
        const reader = new FileReader();
        reader.onload = () => setPreviewImage(reader.result);
        reader.readAsDataURL(rawFile);
    };

    const subordinates = parent?.subordinates || [];
    const roleLabel = profile.position?.name || profile.position_name || 'Employee';
    const divisionLabel = profile.division?.name || profile.division_name || '-';

    return (
        <div className="profile-page responsive-page">
            <div className="profile-page__hero">
                <div className="profile-page__hero-pattern" />
                <div className="profile-page__hero-content">
                    <div className="profile-page__identity">
                        <div className="profile-avatar-wrap">
                            <Avatar
                                className="profile-avatar"
                                size={112}
                                src={previewImage}
                                icon={<UserOutlined />}
                            />
                            <Upload
                                showUploadList={false}
                                beforeUpload={() => false}
                                accept="image/*"
                                onChange={uploadImage}
                            >
                                <Button className="profile-avatar-edit" shape="circle" icon={<CameraOutlined />} aria-label="Ganti foto profile" />
                            </Upload>
                        </div>
                        <div className="profile-page__identity-text">
                            <Text className="profile-page__eyebrow">MY PROFILE</Text>
                            <Title level={2}>{profile.name || 'Profile'}</Title>
                            <div className="profile-page__meta">
                                <Tag>{roleLabel}</Tag>
                                <span>{divisionLabel}</span>
                            </div>
                        </div>
                    </div>
                    <Button type="primary" size="large" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>
                        Save Changes
                    </Button>
                </div>
            </div>

            <Row gutter={[20, 20]} className="profile-page__grid">
                <Col xs={24} lg={16}>
                    <Card className="profile-section-card" bordered={false}>
                        <div className="profile-section-card__heading">
                            <div>
                                <Title level={4}>Personal Information</Title>
                                <Text type="secondary">Perbarui informasi dasar yang digunakan di Bolde Access.</Text>
                            </div>
                        </div>
                        <Divider />
                        <Form form={form} name="form" layout="vertical" onFinish={onFinish}>
                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={12}>
                                    <Form.Item name="name" label="Full Name" rules={[{ required: true, message: 'Full Name is required!' }]}>
                                        <Input size="large" prefix={<UserOutlined />} placeholder="Full name" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Username is required!' }]}>
                                        <Input size="large" prefix={<UserOutlined />} placeholder="Username" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Email is required!' }, { type: 'email', message: 'Email is not valid!' }]}>
                                        <Input size="large" prefix={<MailOutlined />} placeholder="Email address" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item name="phone" label="Phone Number" rules={[{ required: true, message: 'Phone Number is required!' }]}>
                                        <Input size="large" prefix={<PhoneOutlined />} placeholder="Phone number" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    <Card className="profile-summary-card" bordered={false}>
                        <div className="profile-summary-card__icon"><UserOutlined /></div>
                        <Title level={4}>Account Overview</Title>
                        <div className="profile-summary-row">
                            <Text type="secondary">Position</Text>
                            <Text strong>{roleLabel}</Text>
                        </div>
                        <div className="profile-summary-row">
                            <Text type="secondary">Division</Text>
                            <Text strong>{divisionLabel}</Text>
                        </div>
                        <div className="profile-summary-row">
                            <Text type="secondary">Username</Text>
                            <Text strong>{profile.username || '-'}</Text>
                        </div>
                        <div className="profile-summary-row">
                            <Text type="secondary">Status</Text>
                            <Tag color="green">Active</Tag>
                        </div>
                    </Card>
                </Col>
            </Row>

            <Card className="profile-section-card profile-subordinates" bordered={false}>
                <div className="profile-section-card__heading profile-subordinates__heading">
                    <div>
                        <Title level={4}><TeamOutlined /> Subordinates</Title>
                        <Text type="secondary">Tim yang berada di bawah koordinasi Anda.</Text>
                    </div>
                    <Tag>{subordinates.length} People</Tag>
                </div>
                <Divider />
                {subordinates.length > 0 ? (
                    <Row gutter={[16, 16]}>
                        {subordinates.map((child, index) => (
                            <Col xs={24} sm={12} xl={8} xxl={6} key={child.id || child.users_code || index}>
                                <div className="profile-person-card">
                                    <Avatar size={54} src={child.image} icon={<UserOutlined />} />
                                    <div className="profile-person-card__content">
                                        <Text strong>{child.name}</Text>
                                        <Text type="secondary">{child.position?.name || '-'}</Text>
                                        <Text className="profile-person-card__code">{child.users_code || child.username || '-'}</Text>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <div className="profile-empty-team">
                        <TeamOutlined />
                        <Text strong>Belum ada subordinate</Text>
                        <Text type="secondary">Data tim akan muncul di sini saat tersedia.</Text>
                    </div>
                )}
            </Card>
        </div>
    );
}

export default Profile;
